import { useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import { isProTool, toolKey } from "components/Screens/Marketplace/Customer/utils/tools";

interface ToolPickerProps {
  value: string[];
  onChange: (next: string[]) => void;
  /** Chips under "Common apps used by your industry". */
  suggestions: string[];
  /** Typeahead source. */
  options: string[];
  searchPlaceholder?: string;
  label?: string;
  /** Tool logos by toolKey (marketplace tools.logoUrl); a tool without one shows its name only. */
  logos?: ReadonlyMap<string, string>;
}

const MAX_MATCHES = 6;
// The backend's sign-up parser rejects more tools or longer names (plgSignupConfig normalizeTools).
const MAX_TOOLS = 30;
const MAX_TOOL_NAME = 80;
const sameTool = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** Search results: the tool's logo before its name; hidden if it fails to load, so a broken link never shows. */
function ToolLogo({ src }: { src?: string }) {
  if (!src) return null;
  return (
    <img
      className="plg-tool-logo"
      src={src}
      alt=""
      loading="lazy"
      onError={(e) => {
        e.currentTarget.style.display = "none";
      }}
    />
  );
}

/** A chip shows the tool's logo alone when it has one, its name otherwise (never both); a broken logo falls back to the name. */
function ChipFace({ name, src }: { name: string; src?: string }) {
  const [broken, setBroken] = useState(false);
  if (!src || broken) return <>{name}</>;
  return <img className="plg-tool-logo plg-tool-logo--chip" src={src} alt={name} title={name} onError={() => setBroken(true)} />;
}

function ToolPicker({
  value,
  onChange,
  suggestions,
  options,
  searchPlaceholder = "Start typing an app’s name",
  label = "Common apps used by your industry",
  logos,
}: ToolPickerProps) {
  const [query, setQuery] = useState("");
  const logoOf = (tool: string) => logos?.get(toolKey(tool));
  const q = query.trim();

  const matches = useMemo(() => {
    if (!q || value.length >= MAX_TOOLS) return [];
    return options
      .filter((o) => o.length <= MAX_TOOL_NAME && o.toLowerCase().includes(q.toLowerCase()) && !value.some((v) => sameTool(v, o)))
      .slice(0, MAX_MATCHES);
  }, [q, options, value]);

  // Nothing in the list matches: the typed name can be added as it is (product 2026-10-26), unless it is already
  // picked, too long, the list is full, or it is a generic tool kept out of sign-up (Teams, Zoom...: TOOL_DENYLIST).
  // The backend accepts any name within those limits.
  const alreadyPicked = value.some((v) => sameTool(v, q));
  const canAddTyped =
    !!q && !matches.length && value.length < MAX_TOOLS && q.length <= MAX_TOOL_NAME && !alreadyPicked && isProTool(q);

  // Prototype toolPicker: tools picked through search sit in their own row of removable pills, above
  // the common ones (QA ON-7).
  const extras = value.filter((v) => !suggestions.some((s) => sameTool(s, v)));

  const add = (tool: string) => {
    onChange([...value, tool.trim()]);
    setQuery("");
  };
  const toggle = (tool: string) =>
    onChange(value.some((v) => sameTool(v, tool)) ? value.filter((v) => !sameTool(v, tool)) : [...value, tool]);

  return (
    <div className="plg-tool-picker">
      <div className="plg-tool-picker__search">
        <input
          className="plg-tool-picker__input"
          value={query}
          placeholder={searchPlaceholder}
          autoComplete="off"
          onChange={(e) => setQuery(e.target.value)}
        />
        {q && (
          <div className="plg-tool-picker__drop" data-testid="tool-drop">
            {matches.map((m) => (
              <button type="button" key={m} onClick={() => add(m)}>
                <ToolLogo src={logoOf(m)} />
                {m}
              </button>
            ))}
            {canAddTyped && (
              <button type="button" className="plg-tool-picker__custom" onClick={() => add(q)}>
                <span className="plg-tool-picker__custom-name">{q}</span>
                <span className="plg-tool-picker__custom-cta">
                  <AddIcon fontSize="inherit" />
                  Click to add
                </span>
              </button>
            )}
            {!matches.length && !canAddTyped && (
              <div className="plg-tool-picker__none">
                {value.length >= MAX_TOOLS
                  ? `You can add up to ${MAX_TOOLS} apps`
                  : q.length > MAX_TOOL_NAME
                    ? `App names can be up to ${MAX_TOOL_NAME} characters`
                    : alreadyPicked
                      ? "Already added"
                      : "No apps match that name yet"}
              </div>
            )}
          </div>
        )}
      </div>

      {extras.length > 0 && (
        <div className="plg-tool-picker__picked">
          {extras.map((t) => (
            <span key={t}>
              <ChipFace name={t} src={logoOf(t)} />
              <button type="button" aria-label={`Remove ${t}`} onClick={() => toggle(t)}>
                <CloseIcon fontSize="inherit" />
              </button>
            </span>
          ))}
        </div>
      )}

      {suggestions.length > 0 && (
        <>
          <h3 className="plg-tool-picker__label">{label}</h3>
          <div className="plg-tool-picker__sugg">
            {suggestions.map((s) => {
              const on = value.some((v) => sameTool(v, s));
              return (
                <button
                  type="button"
                  key={s}
                  className={`plg-chip${on ? " plg-chip--on" : ""}${logoOf(s) ? " plg-chip--logo" : ""}`}
                  aria-pressed={on}
                  aria-label={s}
                  onClick={() => toggle(s)}
                >
                  <ChipFace name={s} src={logoOf(s)} />
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default ToolPicker;
